import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

// Async thunks
export const fetchVisitRequests = createAsyncThunk(
  'visitRequests/fetchVisitRequests',
  async ({ sellerId, status, page = 0, size = 10 }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/visit-requests/seller/${sellerId}?page=${page}&size=${size}`;
      
      if (status && status !== 'all') {
        url += `&status=${status}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch visit requests');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateVisitRequestStatus = createAsyncThunk(
  'visitRequests/updateVisitRequestStatus',
  async ({ requestId, status, notes }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/visit-requests/${requestId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, notes }),
      });

      if (!response.ok) {
        throw new Error('Failed to update visit request status');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getVisitRequestStats = createAsyncThunk(
  'visitRequests/getVisitRequestStats',
  async ({ sellerId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/visit-requests/stats/seller/${sellerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch visit request stats');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  requests: [],
  stats: {
    pending: 0,
    approved: 0,
    completed: 0,
    cancelled: 0,
    visitDelayed: 0,
    total: 0,
  },
  loading: false,
  error: null,
  statusFilter: 'all',
  pagination: {
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
  },
};

// Slice
const visitRequestSlice = createSlice({
  name: 'visitRequests',
  initialState,
  reducers: {
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearVisitRequests: (state) => {
      state.requests = [];
      state.stats = initialState.stats;
      state.pagination = initialState.pagination;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch visit requests
      .addCase(fetchVisitRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVisitRequests.fulfilled, (state, action) => {
        state.loading = false;
        // Handle the response structure from backend
        const payload = action.payload;
        state.requests = payload.visitRequests || payload.content || payload;
        
        // Update pagination if available
        if (payload.totalPages !== undefined) {
          state.pagination = {
            page: payload.currentPage || 0,
            size: payload.size || 10,
            totalElements: payload.totalItems || 0,
            totalPages: payload.totalPages || 0,
          };
        }
        
        // Update stats if available in analytics
        if (payload.analytics) {
          state.stats = {
            pending: payload.analytics.pendingRequests || 0,
            approved: 0, // Backend doesn't provide this separately
            completed: payload.analytics.completedRequests || 0,
            cancelled: 0, // Backend doesn't provide this separately
            visitDelayed: payload.analytics.visitDelayedRequests || 0,
            total: payload.analytics.totalRequests || 0,
          };
        }
      })
      .addCase(fetchVisitRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update visit request status
      .addCase(updateVisitRequestStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateVisitRequestStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedRequest = action.payload;
        const index = state.requests.findIndex(req => req.id === updatedRequest.id);
        if (index !== -1) {
          state.requests[index] = updatedRequest;
        }
      })
      .addCase(updateVisitRequestStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Get visit request stats
      .addCase(getVisitRequestStats.pending, (state) => {
        state.error = null;
      })
      .addCase(getVisitRequestStats.fulfilled, (state, action) => {
        const payload = action.payload;
        // Backend wraps stats in a 'stats' object
        const stats = payload.stats || payload;
        state.stats = {
          pending: stats.pending || 0,
          approved: stats.approved || 0,
          completed: stats.completed || 0,
          cancelled: stats.cancelled || 0,
          visitDelayed: stats.visitDelayed || 0,
          total: stats.total || 0,
        };
      })
      .addCase(getVisitRequestStats.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { setStatusFilter, clearError, clearVisitRequests } = visitRequestSlice.actions;
export default visitRequestSlice.reducer;